import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, CircularProgress, Alert, List, ListItem, Divider, Chip,
} from "@mui/material";
import { fetchAllSessions } from "../../services/VcatUserSession/service";
import { fetchVcatUser } from "../../services/VcatUser/service";
import { GroupedVCatUserSessionResponse } from "../../types/models/vcatSession";
import { formatDistanceToNowStrict, parseISO } from "date-fns";

const Sessions: React.FC = () => {
  const [sessions, setSessions] = useState<GroupedVCatUserSessionResponse[]>([]);
  const [usernames, setUsernames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const data = await fetchAllSessions();
        setSessions(data);

        const uniqueUserIds = Array.from(new Set(data.map((group) => group.user_id)));

        const fetchedUsernames: Record<number, string> = {};
        await Promise.all(
          uniqueUserIds.map(async (userId) => {
            try {
              const user = await fetchVcatUser(userId);
              fetchedUsernames[userId] = user.username;
            } catch (err) {
              console.error(`Failed to fetch username for user ID ${userId}`, err);
              fetchedUsernames[userId] = `User ${userId}`;
            }
          })
        );
        setUsernames(fetchedUsernames);
      } catch (err) {
        setError((err as Error).message || "Failed to load sessions.");
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "#0D1117", minHeight: "100vh" }}>
      <Typography variant="h4" gutterBottom sx={{ color: "#FFFFFF" }}>
        Sessions  
      </Typography>

      <Paper elevation={3} sx={{ p: 2, backgroundColor: "#161B22", borderRadius: "8px" }}>
        <List>
          {sessions.map((group) => (
            <React.Fragment key={group.user_id}>
              <ListItem
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1,
                  padding: 2,
                  borderRadius: 3,
                  backgroundColor: "#1C232A",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: "bold" }}>
                  {usernames[group.user_id] || `User ${group.user_id}`}
                </Typography>

                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                  {group.sessions.map((session) => (
                    <Paper
                      key={session.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#24292E",
                        color: "#A8B2D1",
                        transition: "background-color 0.3s ease",
                        "&:hover": {
                          backgroundColor: "#2F353A",
                          boxShadow: "0 0 10px rgba(180, 245, 2, 0.24)",
                          cursor: "pointer",
                        },
                      }}
                    >


                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#86B3FF" }}>
                        Serial: {session.serial}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                        <Chip
                          label={`Start: ${new Date(session.start_time).toLocaleString()}`}
                          sx={{
                            backgroundColor: "transparent",
                            border: "1px solid #64FFDA",
                            color: "#64FFDA",
                            fontWeight: "bold",
                          }}
                        />
                        <Chip
                          label={session.end_time ? `End: ${new Date(session.end_time).toLocaleString()}` : "Active"}
                          sx={{
                            backgroundColor: session.end_time ? "#FF4B4B" : "#4EDDC7",
                            color: "#161B22",
                          }}
                        />
                        <Chip
                          label={`Last Ping: ${session.last_ping ? formatDistanceToNowStrict(parseISO(session.last_ping)) + " ago" : "N/A"}`}
                          sx={{ backgroundColor: "#86B3FF", color: "white" }}
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </ListItem>
              <Divider sx={{ backgroundColor: "#2E3742" }} />
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default Sessions;
